/**
 * ImageService.js
 * A service for handling image loading and management throughout the application
 */

// Import all images
import img1_1 from '../assets/images/1.1.jpg';
import img1_2 from '../assets/images/1.2.jpg';
import img2_1 from '../assets/images/2.1.jpg';
import img2_2 from '../assets/images/2.2.jpg';
import img2_3 from '../assets/images/2.3.jpg';
import img2_4 from '../assets/images/2.4.jpg';
import img2 from '../assets/images/2.jpg';
import img3_1 from '../assets/images/3.1.jpg';
import img3_2 from '../assets/images/3.2.jpg';
import img3_3 from '../assets/images/3.3.jpg';
import img3_4 from '../assets/images/3.4.jpg';
import img3 from '../assets/images/3.jpg';
import img4_1 from '../assets/images/4.1.jpg';
import img4_2 from '../assets/images/4.2.jpg';
import img4_3 from '../assets/images/4.3.jpg';
import img4 from '../assets/images/4.jpg';
import img5_1 from '../assets/images/5.1.jpg';
import img5_2 from '../assets/images/5.2.jpg';
import img5_4 from '../assets/images/5.4.jpg';
import img5 from '../assets/images/5.jpg';
import img6_1 from '../assets/images/6.1.jpg';
import img6_2 from '../assets/images/6.2.jpg';
import img6_3 from '../assets/images/6.3.jpg';
import img6 from '../assets/images/6.jpg';

// Create an image map for easy lookup
const imageMap = {
  // Property 1
  '1.1': img1_1,
  '1.2': img1_2,
  
  // Property 2
  '2': img2,
  '2.1': img2_1,
  '2.2': img2_2,
  '2.3': img2_3,
  '2.4': img2_4,
  
  // Property 3
  '3': img3,
  '3.1': img3_1,
  '3.2': img3_2,
  '3.3': img3_3,
  '3.4': img3_4,
  
  // Property 4
  '4': img4,
  '4.1': img4_1,
  '4.2': img4_2,
  '4.3': img4_3,
  
  // Property 5
  '5': img5,
  '5.1': img5_1,
  '5.2': img5_2,
  '5.4': img5_4,
  
  // Property 6
  '6': img6,
  '6.1': img6_1,
  '6.2': img6_2,
  '6.3': img6_3,
};

/**
 * Get an image by its key
 * @param {string} key - The image key (e.g., '1.1', '2.3')
 * @returns {string|object} The image source
 */
export const getImage = (key) => {
  if (!key) return null;
  
  // If the key is already an imported image object, return it directly
  if (typeof key === 'object') return key;
  
  // If the key is a string that represents a path, return it directly
  if (typeof key === 'string' && (key.startsWith('http') || key.startsWith('/'))) {
    return key;
  }
  
  // Otherwise, look up the image in our map
  return imageMap[key] || null;
};

/**
 * Get all property images for a specific property
 * @param {number} propertyId - The property ID
 * @returns {Object} An object containing the main image and additional images
 */
export const getPropertyImages = (propertyId) => {
  switch (propertyId) {
    case 1:
      return {
        main: getImage('1.1'),
        additional: [getImage('1.2'), getImage('1.1')]
      };
    case 2:
      return {
        main: getImage('2.1'),
        additional: [getImage('2.2'), getImage('2.3'), getImage('2.4'), getImage('2'), getImage('2.1')]
      };
    case 3:
      return {
        main: getImage('3.1'),
        additional: [getImage('3.2'), getImage('3.3'), getImage('3.4'), getImage('3'), getImage('3.1')]
      };
    case 4:
      return {
        main: getImage('4'),
        additional: [getImage('4.1'), getImage('4.2'), getImage('4'), getImage('4.3')]
      };
    case 5:
      return {
        main: getImage('5.4'),
        additional: [getImage('5.1'), getImage('5.2'), getImage('5.4'), getImage('5')]
      };
    case 6:
      return {
        main: getImage('6.1'),
        additional: [getImage('6.2'), getImage('6.3'), getImage('6.1'), getImage('6')]
      };
    default:
      return {
        main: null,
        additional: []
      };
  }
};

/**
 * Get all images for all properties
 * @returns {Array} An array of property objects with their images
 */
export const getAllPropertyImages = () => [
  {
    id: 1,
    image: getImage('1.1'),
    additionalImages: [getImage('1.2'), getImage('1.1')]
  },
  {
    id: 2,
    image: getImage('2.1'),
    additionalImages: [getImage('2.2'), getImage('2.3'), getImage('2.4'), getImage('2'), getImage('2.1')]
  },
  {
    id: 3,
    image: getImage('3.1'),
    additionalImages: [getImage('3.2'), getImage('3.3'), getImage('3.4'), getImage('3'), getImage('3.1')]
  },
  {
    id: 4,
    image: getImage('4'),
    additionalImages: [getImage('4.1'), getImage('4.2'), getImage('4'), getImage('4.3')]
  },
  {
    id: 5,
    image: getImage('5.4'),
    additionalImages: [getImage('5.1'), getImage('5.2'), getImage('5.4'), getImage('5')]
  },
  {
    id: 6,
    image: getImage('6.1'),
    additionalImages: [getImage('6.2'), getImage('6.3'), getImage('6.1'), getImage('6')]
  }
];

export default {
  getImage,
  getPropertyImages,
  getAllPropertyImages,
  imageMap
};